import { Component, inject, signal, } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Case, ItemListResponse, Placename } from '@models';
import { NotificationService, PlacenameService, TitleService } from '@services';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatIconModule } from '@angular/material/icon';
import geojson from 'geojson';
import { ListComponent } from '@components/list/list.component';
import { MatListModule, MatListOption } from '@angular/material/list';
import { DatePipe } from '@angular/common';
import { MatPaginatorModule } from '@angular/material/paginator';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { environment } from '@environment';
import { MatDialog } from '@angular/material/dialog';
import { ResourcePickerComponent } from '@components/resource-picker/resource-picker.component';
import { MatTabsModule } from '@angular/material/tabs';

@Component({
  selector: 'app-placename-form',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    DatePipe,

    MatIconModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatButtonModule,
    MatListModule,
    MatPaginatorModule,
    MatTabsModule,

    ListComponent,
  ],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss'
})
export class PlacenameFormComponent {
  route = inject(ActivatedRoute);
  router = inject(Router);
  placenameService = inject(PlacenameService);
  notificationService = inject(NotificationService);
  titleService = inject(TitleService);
  dialog = inject(MatDialog);

  separatorKeysCodes = [ENTER, COMMA] as const;
  placename: Placename | null = this.route.snapshot.data['placename'] || null;
  form = new FormGroup({
    title: new FormControl(this.placename?.title, [Validators.required]),
    area: new FormControl(this.placename?.area),
    status: new FormControl(this.placename?.status, [Validators.required]),
    type: new FormControl(this.placename?.type),
    terrain: new FormControl(this.placename?.terrain),
    country: new FormControl(this.placename?.country, [Validators.required]),
    authority: new FormControl(this.placename?.authority),
    description: new FormControl(this.placename?.description),
    origin: new FormControl(this.placename?.origin),
    comment: new FormControl(this.placename?.comment),
    start: new FormControl(this.placename?.start),
    end: new FormControl(this.placename?.end),
    proposer: new FormControl(this.placename?.proposer),
    geometry: new FormControl(this.placename?.geometry.coordinates.join(', '),
      [
        Validators.required,
        Validators.pattern(
          /^([-+]?\d+(?:\.\d+)?)(?:,\s?)([-+]?\d+(?:\.\d+)?)((?:,\s?)[-+]?\d+(?:\.\d+)?)?$/
        )
      ]),
    keywords: new FormControl(this.placename?.keywords || []),
  });

  casesPage = signal(0);
  cases = httpResource<ItemListResponse<Case>>(() => this.placename
    ? `${environment.kinkoHost}/case/?skip=${this.casesPage() * 10}&take=10&count=true&order=created:desc&placename=${this.placename.id}`
    : undefined);

  alternativePage = signal(0);
  alternative = httpResource<ItemListResponse<Placename>>(() => this.placename
    ? `${environment.kinkoHost}/placename/?skip=${this.alternativePage() * 10}&take=10&count=true&order=created:desc&alternative=${this.placename.id}`
    : undefined);

  replacementsPage = signal(0)
  replacements = httpResource<ItemListResponse<Placename>>(() => this.placename
    ? `${environment.kinkoHost}/placename/?skip=${this.replacementsPage() * 10}&take=10&count=true&order=created:desc&replacedBy=${this.placename.id}`
    : undefined);

  save() {
    const formValue = this.form.value;
    const p = {
      id: this.placename?.id,
      title: formValue.title,
      area: formValue.area || undefined,
      status: formValue.status,
      type: formValue.type || undefined,
      terrain: formValue.terrain || undefined,
      country: formValue.country,
      authority: formValue.authority || undefined,
      description: formValue.description || undefined,
      origin: formValue.origin || undefined,
      comment: formValue.comment || undefined,
      start: formValue.start || undefined,
      end: formValue.end || undefined,
      proposer: formValue.proposer || undefined,
      geometry: {
        type: "Point",
        coordinates: formValue.geometry?.split(',').map(v => Number(v)) as geojson.Position
      } as geojson.Point,
      keywords: formValue.keywords || undefined,
    } as Placename;

    const fn = this.placename?.id
      ? this.placenameService.update(p)
      : this.placenameService.create(p);

    fn.subscribe({
      next: (result: Placename) => {
        this.router.navigate(['placename', result.id, 'edit']).finally(() => {
          this.form.markAsPristine();
        });
      },
      error: (err) => {
        this.notificationService.error('Failed to save changes.');
        console.error(err);
      }
    });
  }

  addKeyword(event: MatChipInputEvent) {
    const keyword = (event.value || '').trim();

    const formControl = this.form.get('keywords');
    const keywords = formControl?.value || [];
    if (!keyword || keywords.includes(keyword)) {
      return;
    }

    this.form.patchValue({ keywords: [...keywords, keyword] });
    event.chipInput.clear();
    formControl?.markAsDirty()
  }

  removeKeyword(keyword: string) {
    const formControl = this.form.get('keywords');
    const keywords = (formControl?.value || []).filter(k => k !== keyword);

    this.form.patchValue({ keywords: keywords });
    formControl?.markAsDirty()
  }


  addCase() {
    this.dialog.open(ResourcePickerComponent<Case>, {
      minWidth: '360px',
      width: '480px',
      autoFocus: false,
      data: {
        title: "Add case reference",
        description: "Select a case connected to this placename.",
        label: "Case title",
        placeholder: "Search in cases",
        endpoint: `${environment.kinkoHost}/case/?skip=0&take=10&order=created:desc`
      },
    }).afterClosed().subscribe((result?: Case) => {
      if (!result || !this.placename) {
        return;
      }

      this.placenameService.addCase(this.placename.id, result.id).subscribe({
        next: () => {
          this.cases.reload();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            this.notificationService.warning('Selected case is already in the list.');
          } else {
            this.notificationService.error('Failed to save changes.');
          }
          console.error(err);
        }
      });
    });
  }

  removeCases(options: MatListOption[]) {
    if (!this.placename) {
      return;
    }

    const ids = options.map((o) => o.value as string);
    for (const id of ids) {
      this.placenameService.removeCase(this.placename.id, id).subscribe({
        next: () => {
          this.cases.reload();
        },
        error: (err: HttpErrorResponse) => {
          this.notificationService.error('Failed to save changes.');
          console.error(err);
        }
      });
    }
  }

  addSameAs() {
    this.dialog.open(ResourcePickerComponent<Placename>, {
      minWidth: '360px',
      width: '480px',
      autoFocus: false,
      data: {
        title: "Add placename reference",
        description: "Select an alternative placename for this area. E.g. a name assigned by a different naming authority.",
        label: "Placename title",
        placeholder: "Search in placenames",
        endpoint: `${environment.kinkoHost}/placename/?skip=0&take=10&order=created:desc`
      },
    }).afterClosed().subscribe((result?: Placename) => {
      if (!result || !this.placename) {
        return;
      }

      this.placenameService.addSameAs(this.placename.id, result.id).subscribe({
        next: () => {
          this.alternative.reload();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            this.notificationService.warning('Selected placename is already in the list.');
          } else {
            this.notificationService.error('Failed to save changes.');
          }
          console.error(err);
        }
      });
    });
  }

  removeSameAs(options: MatListOption[]) {
    if (!this.placename) {
      return;
    }

    const ids = options.map((o) => o.value as string);
    for (const id of ids) {
      this.placenameService.removeSameAs(this.placename.id, id).subscribe({
        next: () => {
          this.alternative.reload();
        },
        error: (err: HttpErrorResponse) => {
          this.notificationService.error('Failed to save changes.');
          console.error(err);
        }
      });
    }
  }

  addReplacement() {
    this.dialog.open(ResourcePickerComponent<Placename>, {
      minWidth: '360px',
      width: '480px',
      autoFocus: false,
      data: {
        title: "Add placename reference",
        description: "Select another placename which is considered replaced by this one.",
        label: "Placename title",
        placeholder: "Search in placenames",
        endpoint: `${environment.kinkoHost}/placename/?skip=0&take=10&order=created:desc`
      },
    }).afterClosed().subscribe((result?: Placename) => {
      if (!result || !this.placename) {
        return;
      }

      this.placenameService.addReplacement(this.placename.id, result.id).subscribe({
        next: () => {
          this.replacements.reload();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            this.notificationService.warning('Selected placename is already in the list.');
          } else {
            this.notificationService.error('Failed to save changes.');
          }
          console.error(err);
        }
      });
    });
  }

  removeReplacements(options: MatListOption[]) {
    if (!this.placename) {
      return;
    }

    const ids = options.map((o) => o.value as string);
    for (const id of ids) {
      this.placenameService.removeReplacement(this.placename.id, id).subscribe({
        next: () => {
          this.replacements.reload();
        },
        error: (err: HttpErrorResponse) => {
          this.notificationService.error('Failed to save changes.');
          console.error(err);
        }
      });
    }
  }
}
